<template>
    <div class="cooking-product-card" :class="{ 'selected': selected, 'collapsed': collapsed, [$mq]: $mq }" @click="selectProduct">
        <i class="i-andy-done i-andy-m" v-if="selected && !collapsed"></i>
        <div class="card-info" v-if="!collapsed">
            <p>{{ product.quantity}} {{ product.name }}</p>
            <span>En {{ formatDate() }}</span>
        </div>
        <div class="card-timer">
            <div class="circular-progress">
                <CountDown :nextAlarm="getTime()" :small="collapsed" @timerCompleted="onTimerCompleted"/>
            </div>
        </div>
        <div class="progress-bar" v-if="!collapsed">
            <div class="progress-bar-fill" :style="{ width: progress + '%' }"></div>
        </div>
    </div>
</template>

<script>
import { formatDisplayDateFromStore } from '@/utils/dateFormatters'
import CountDown from './CountDown.vue';

export default {
    components: {
        CountDown
    },
    props: {
        product: {
            type: Object,
            required: true
        },
        isSelected: {
            type: Boolean,
            default: false
        },
        collapsed: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            date: new Date(),
            progress: 0,
            progressTimer: null
        }
    },
    computed: {
        selected() {
            const selectedProducts = this.$store.getters['production/getSelectedCookingProducts']
            return selectedProducts.some(p => p.batch_id === this.product.batch_id)
        }
    },
    methods: {
        parseDateToMs(input) {
            if (!input && input !== 0) return null
            // Si es número o string numérica, detectar segundos vs milisegundos
            if (typeof input === 'number' || (typeof input === 'string' && /^\d+$/.test(input))) {
                const numericValue = Number(input)
                // Considerar < 1e12 como segundos (10 dígitos) y >= 1e12 como milisegundos
                return numericValue < 1e12 ? numericValue * 1000 : numericValue
            }
            // Fallback: parsear como fecha
            const parsed = new Date(input).getTime()
            return isNaN(parsed) ? null : parsed
        },
        getTime() {
            // Usar ready_date como instante objetivo y created_date como referencia de inicio.
            if (this.product.ready_date && this.product.created_date) {
                const readyAt = this.parseDateToMs(this.product.ready_date)
                const now = Date.now()
                return now >= readyAt ? now : readyAt
            } else {
                // Fallback: si no hay ready_date, usar created_date + time (min)
                const start = this.product.created_date ? this.parseDateToMs(this.product.created_date) : Date.now()
                const duration = Number(this.product.time || 0) * 60 * 1000
                const end = start + duration
                const now = Date.now()
                return now >= end ? now : end
            }
        },
        updateProgress() {
            // Calcular progreso basado en (now - created_date) / (ready_date - created_date)
            let start = null
            let end = null

            if (this.product.created_date) {
                start = this.parseDateToMs(this.product.created_date)
            }

            if (this.product.ready_date) {
                end = this.parseDateToMs(this.product.ready_date)
            } else if (this.product.time) {
                // Fallback: duration por minutos si no hay ready_date
                const base = (start !== null && start !== undefined) ? start : Date.now()
                end = base + Number(this.product.time) * 60 * 1000
            }

            if (start == null || end == null || end <= start) {
                this.progress = 0
                return
            }

            const now = Date.now()
            const total = end - start
            const elapsed = Math.min(Math.max(now - start, 0), total)
            this.progress = Math.min(Math.max((elapsed / total) * 100, 0), 100)

            if (now >= end && this.progressTimer) {
                clearInterval(this.progressTimer)
                this.progressTimer = null
            }
        },
        formatDate() {
            return formatDisplayDateFromStore(this.product.ready_date || this.date, { includeTime: true })
        },
        onTimerCompleted() {
            // Prevenir múltiples emisiones del mismo evento
            if (this.product._timerCompleted) {
                return
            }
            
            // Marcar como completado para evitar múltiples emisiones
            this.product._timerCompleted = true
            
            // Emitir evento al componente padre cuando el timer termina
            this.$emit('cookingCompleted', this.product)
        },
        selectProduct() {
            if(this.selected){
                this.$store.commit('production/removeSelectedCookingProduct', this.product.batch_id)
            } else {
                this.$store.commit('production/addSelectedCookingProduct', this.product)
            }
        }
    },
    mounted() {
        this.updateProgress()
        this.progressTimer = setInterval(() => this.updateProgress(), 1000)
    },
    beforeDestroy() {
        if (this.progressTimer) {
            clearInterval(this.progressTimer)
            this.progressTimer = null
        }
    }
}
</script>

<style lang="scss" scoped>
.cooking-product-card {
    width: 100%;
    padding: 16px;
    @include display-flex();
    @include flex-direction(row);
    @include align-items(center);
    @include justify-content(space-between);
    gap: 8px;
    border-radius: 4px;
    background-color: $color-warning-100;
    position: relative;

    &.collapsed {
        padding: 8px;
        @include justify-content(center);
        min-width: 0;
        height: 100%;
        min-height: 0;

        .card-timer {
            transform: scale(0.55);
            transform-origin: center;
        }
    }
    &.selected{
        position: relative;
        border: 2px solid $color-info-500;

        >i[class^="i-andy-"]{
            position: absolute;
            top: 0;
            right: 0;
            color: $color-white;
            background-color: $color-info-500;
            padding: 5px 5px 36px 36px;
            clip-path: polygon(0 0, 100% 0, 100% 100%);
            z-index: 1;
        }

        .card-info{
            filter: blur(1px);
        }

        .card-timer{
            filter: blur(1px);
        }

        .progress-bar{
            filter: blur(1px);
        }
    }

    .card-info {
        @include display-flex();
        @include flex-direction(column);
        gap: 4px;

        p {
            font-family: $text-bold;
            text-transform: uppercase;
            @include font-size(28px);
            color: $color-black;
            line-height: 1;
            padding: unset;
        }

        span {
            font-family: $text;
            @include font-size(14px);
            color: $color-warning-800;
        }
    }

    .progress-bar{
        position: absolute;
        bottom: 16px;
        left: 16px;
        width: calc(100% - 32px);
        height: 8px;
        background-color: red;
        border-radius: 4px;
        z-index: 2;
        background-color: $color-white;
        box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.014);
        .progress-bar-fill{
            background-color: $color-warning-500;
            border-radius: 4px;
                height: 100%;
                transition: width 0.3s linear;
        }
    }

    /* PLS SIZES MONITORS*/
    &.landscape {
        .card-info {
            p {
                @include font-size(18px);

            }

            span {
                @include font-size(14px);
            }
        }

        .progress-bar{
            bottom: 6px;
            width: calc(100% - 10px); 
            left: 6px;
            height: 6px;
        }
    }
}
</style>
